// @flow

import React, { type Node, useCallback } from 'react';
import styled, { css } from 'styled-components';

import { useOpenedIncomingPayment, getSafeCheckoutPaymentOptions } from '@tectonics/prs/incoming-payment';
import { useUserInputValue } from '@tectonics/prs/user-input-values';
import { useFormHasBeenSent } from '@tectonics/prs/form-status';
import { useUserInputValidationError, type UserInputValidationError, serverErrorMessage } from '@tectonics/prs/validation';
import { useSubmitting } from '@tectonics/prs/submitting';
import { useUniqueId } from '@tectonics/hooks';

import { RadioButton } from '../../../../../atoms';
import { ErrorLabel } from '../../../../../molecules/error-label';

import { getSupportedTraits } from '@tectonics/prs/feature';
import lookAndFeel from '@tectonics/look-and-feel/volcanes';
import { intersect } from '@tectonics/commons/arrays';
import { getEWalletCheckoutPaymentOptions } from '@tectonics/prs/incoming-payment/checkout-payment-options';
import { getSafeSupportedFeatureByName } from '@tectonics/prs/support';

import mercadoPagoIconSrc from './icons/mercado-pago-icon.svg';
import picpayIconSrc from './icons/picpay-icon.svg';
import ameIconSrc from './icons/ame-icon.svg';
import paypalIconSrc from './icons/paypal-icon.svg';
import machpaylIconSrc from './icons/machpay-icon.svg';
import mpesaIconSrc from './icons/mpesa-icon.svg';
import ussdIconSrc from './icons/ussd-icon.svg';
import airtelIconSrc from './icons/airtel-icon.svg';
import equitelIconSrc from './icons/equitel-icon.svg';
import vodacomIconSrc from './icons/vodacom-icon.svg';
import tigoIconSrc from './icons/tigo-icon.svg';
import upiIconSrc from './icons/upi-icon.svg';
import mtnIconSrc from './icons/mtn-icon.svg';
import zamtelIconSrc from './icons/zamtel-icon.svg';
import { useIsMobile } from '@tectonics/hooks/use-is-mobile';

export function EWalletList(): Node {
  const incomingPayment = useOpenedIncomingPayment();
  let checkoutPaymentOptions = getSafeCheckoutPaymentOptions(incomingPayment);
  const ewalletAllowedBrands = getEWalletCheckoutPaymentOptions(checkoutPaymentOptions);

  const supportedDisplayEWalletBrandFeature = getSafeSupportedFeatureByName(
    'display_ewallet_accepts_brand',
    lookAndFeel.prsSupport
  );

  const supportedBrands = getSupportedTraits(supportedDisplayEWalletBrandFeature);
  let brandsToDisplay = intersect(supportedBrands, ewalletAllowedBrands);
  const [selectedEWallet, setSelectedEWallet] = useUserInputValue('selectedEWallet');
  const formHasBeenSent = useFormHasBeenSent();
  const validationError = useUserInputValidationError('selectedEWallet');
  const submitting = useSubmitting();

  const inputId = useUniqueId();
  const onChange = useCallback(e => setSelectedEWallet(e.target.value), [setSelectedEWallet]);

  if (selectedEWallet === '' && brandsToDisplay.length === 1) {
    setSelectedEWallet(brandsToDisplay[0]);
  }

  let upiIntentIndex = brandsToDisplay.indexOf('upi-intent');
  if (!useIsMobile() && upiIntentIndex != -1) {
    brandsToDisplay = [...brandsToDisplay];
    brandsToDisplay.splice(upiIntentIndex);
  }

  return (
    <InputList>
      <div data-test-name="payment-type-code" style={{ gridColumn: '1/3' }}>
        <EWalletListGroup name="payment-type-code" id="payment-type-code">
          {brandsToDisplay.map(brand => (
            <EWalletItem htmlFor={`${inputId}-${brand}`} key={brand}>
              <RadioButton
                id={`${inputId}-${brand}`}
                name="payment_type_code"
                value={brand}
                checked={brand === selectedEWallet}
                onChange={onChange}
                disabled={submitting}
                data-error={formHasBeenSent && validationError ? 'error' : null}
                data-test-value={brand}
              />
              <EWalletNameWrapper>{getEWalletName(brand)}</EWalletNameWrapper>
              <EWalletIcon src={getEWalletIconSrc(brand)} alt={brand} />
            </EWalletItem>
          ))}
        </EWalletListGroup>
        {formHasBeenSent && validationError && <ValidationMessage validationError={validationError} />}
      </div>
    </InputList>
  );
}

const EWalletListGroup = styled.div`
  border-radius: 5px;
  box-shadow: 0 0 0 1px rgba(63,63,68,0.05), 0 1px 3px 0 rgba(63,63,68,0.15);
  ${props => props.focused && css`
    box-shadow: 0 0 3px 1px ${props => props.theme.colors.primary(50)};
  `}
  color: ${props => props.theme.colors.primary(10)};
  max-height: 200px;
  overflow-x: hidden;
`;

const EWalletItem = styled.label`
  position: relative;
  display: flex;
  cursor: pointer;
  padding: 16px;
  align-items: center;

  &:first-child {
    border-top-left-radius: 5px;
    border-top-right-radius: 5px;
  }

  &:last-child {
    border-bottom-left-radius: 5px;
    border-bottom-right-radius: 5px;
  }

  ${props => props.selected && css`
    background: ${props => props.theme.colors.gray(96)};
  `}
`;

const InputList = styled.div`
  display: grid;
  grid-gap: 2rem;
  grid-template-columns: 1fr 1fr;
  grid-auto-flow: dense;

  & > * {
    grid-column: 1/3;
  }
`;

const EWalletNameWrapper = styled.span`
  margin-left: 12px;
  font-size: 0.9em;
  font-weight: 600;
  flex-grow: 1;
  color: ${props => props.theme.colors.secondary(30)};
`;

const EWalletIcon = styled.img`
  height: 20px;
  width: auto;
  margin-left: auto;
`;

type ValidationMessageProps = {|
  validationError: UserInputValidationError,
|};

function ValidationMessage({ validationError }: ValidationMessageProps): Node {
  switch (validationError.id) {
    case 'empty':
      return <ErrorLabel>Selecione o seu banco</ErrorLabel>;

    case 'server-error':
      return <ErrorLabel>{serverErrorMessage(validationError.payload)}</ErrorLabel>;

    default:
      return <ErrorLabel>Erro na seleção do banco</ErrorLabel>;
  }
}

function getEWalletName(ewallet: string): string {
  switch (ewallet) {
    case 'mercadopago':
      return 'Mercado Pago';

    case 'picpay':
      return 'PicPay';

    case 'ame':
      return 'Ame Digital';

    case 'paypal':
      return 'PayPal';

    case 'machpay':
      return 'MACH';

    case 'mpesa':
      return 'M-Pesa';

    case 'ussd':
      return 'USSD';

    case 'airtel':
      return 'Airtel';

    case 'equitel':
      return 'Equitel';

    case 'vodacom':
      return 'Vodacom M-PESA';

    case 'tigo':
      return 'Tigo Pesa';

    case 'upi-qrcode':
      return 'Scan QR Code';

    case 'upi-collect':
      return 'UPI ID';

    case 'upi-intent':
      return 'UPI APPS';

    case 'mtn':
      return 'MTN MoMo';

    case 'zamtel':
      return 'Zamtel';

    default:
      throw new Error(`EWalletList - ewallet not found ${ewallet}`);
  }
}

function getEWalletIconSrc(ewallet: string): string {
  switch (ewallet) {
    case 'mercadopago':
      return mercadoPagoIconSrc;

    case 'picpay':
      return picpayIconSrc;

    case 'ame':
      return ameIconSrc;

    case 'paypal':
      return paypalIconSrc;

    case 'machpay':
      return machpaylIconSrc;

    case 'mpesa':
      return mpesaIconSrc;

    case 'ussd':
      return ussdIconSrc;

    case 'airtel':
      return airtelIconSrc;

    case 'equitel':
      return equitelIconSrc;

    case 'upi-qrcode':
      return upiIconSrc;

    case 'upi-collect':
      return upiIconSrc;

    case 'upi-intent':
      return upiIconSrc;

    case 'vodacom':
      return vodacomIconSrc;

    case 'tigo':
      return tigoIconSrc;

    case 'mtn':
      return mtnIconSrc;

    case 'zamtel':
      return zamtelIconSrc;

    default:
      throw new Error(`EWalletList - Image source not found for ${ewallet}`);
  }
}
